<template>
  <section style="width: 100%">
    <v-row align="center" no-gutters class="mb-3">
      <v-col cols="auto" class="pl-0">
        <div class="title text--secondary mt-2 px-4 text-uppercase">
          {{ $t("instructions.title") }}
        </div>
      </v-col>
      <v-col class="text-end">
        <change-instruction-group :instructions="instructions" />
        <v-btn
          v-if="role('admin', 'safetyRepresentative')"
          class="primary pl-6"
          @click="editInstruction = 0"
          data-test="instruction-upload"
        >
          <v-icon dark left>note_add</v-icon>
          {{ $t("instructions.upload") }}
        </v-btn>
      </v-col>
    </v-row>

    <instructions-table
      :instructions="instructions"
      :canEdit="role('admin', 'safetyRepresentative')"
      :loading="$apollo.loading || loading"
      :showActiveCounts="false"
      @edit="(id) => (this.editInstruction = +id)"
      @setIsActive="setIsActive"
      @addOrEditTest="addOrEditTest"
      @alert="(e) => (alert = e)"
      @showActive="(e) => (showActive = !!e)"
      @forceRereading="forceRereading"
    />

    <instruction-edit
      :instructionId.sync="editInstruction"
      @updated="instructionUpdated"
    />

    <test-edit-dialog
      :test="editTest"
      :instructionId="testInstructionId"
      @test-done="testDone"
    />
    <alert
      :alert="alert"
      :title="$t('tests.testEdit.remove')"
      :text="
        `
        ${$t('tests.testEdit.removeFromInstruction')} ${alert && alert.code}
        `
      "
      :okText="$t('tests.testEdit.remove')"
      @cancel="alert = null"
      @ok="deleteTest($event)"
    />
    <force-rereading-dialog
      :show="forceRereadingDialog.show"
      :instruction="forceRereadingDialog.instruction"
      @confirm="forceRereadingDialog.show = false"
      @hide="forceRereadingDialog.show = false"
    />
  </section>
</template>

<script>
import { mapGetters } from "vuex";

import InstructionEdit from "@/components/InstructionEdit";
import TestEditDialog from "@/components/TestEditDialog";
import Alert from "@/components/Alert";
import InstructionsTable from "@/components/instruction/InstructionsTable";
import ChangeInstructionGroup from "@/components/instruction/ChangeInstructionGroup";
import ForceRereadingDialog from "@/components/instruction/ForceRereadingDialog";

import InstructionsByIsActiveQuery from "@/graphql/queries/instruction/InstructionsByIsActive.gql";
import InstructionSetIsActiveQuery from "@/graphql/InstructionSetIsActive.gql";
import TestQuery from "@/graphql/queries/instruction/test/Test.gql";
import AssignTestQuery from "@/graphql/AssignTestToInstruction.gql";
import DeleteTestQuery from "@/graphql/DeleteTest.gql";

export default {
  components: {
    InstructionEdit,
    TestEditDialog,
    Alert,
    InstructionsTable,
    ChangeInstructionGroup,
    ForceRereadingDialog,
  },
  data() {
    return {
      instructions: [],
      editInstruction: null,
      editTest: null,
      loading: false,
      testInstructionId: null,
      alert: null,
      showActive: true,
      forceRereadingDialog: {
        show: false,
        instruction: null,
        callback: () => {},
      },
    };
  },
  computed: {
    ...mapGetters(["role"]),
  },

  methods: {
    deleteTest(args) {
      if (args.length < 2) {
        this.$store.commit("push_global_message", {
          color: "error",
          text: `failed delete test`,
        });
        return;
      }
      this.loading = true;
      this.$apollo
        .mutate({
          mutation: AssignTestQuery,
          variables: {
            instructionId: args.instructionId,
            testId: null,
          },
        })
        .catch((err) =>
          this.$store.commit("push_global_message", {
            color: "error",
            text: `failed to detach test from instruction: ${err}`,
          })
        );

      this.$apollo
        .mutate({
          mutation: DeleteTestQuery,
          variables: {
            id: args.testId,
          },
        })
        .catch((err) =>
          this.$store.commit("push_global_message", {
            color: "error",
            text: `failed to delete test: ${err}`,
          })
        )
        .finally(() => {
          this.loading = false;
          this.$apollo.queries.instructions.refresh();
          this.alert = null;
        });
    },

    addOrEditTest(id, instructionId, instructionCode) {
      this.testInstructionId = instructionId;
      if (!id) {
        this.editTest = {
          name: instructionCode + " test" || "",
          questions: [],
        };
        return;
      }
      this.loading = true;

      this.$apollo
        .mutate({
          mutation: TestQuery,
          variables: { id },
        })
        .then((res) => {
          this.editTest = res.data.test;
        })
        .finally(() => {
          this.loading = false;
        });
    },

    testDone() {
      this.editTest = null;
      this.$nextTick(() => {
        this.$apollo.queries.instructions.refresh();
      });
    },

    instructionUpdated() {
      this.$apollo.queries.instructions.refresh();
    },

    setIsActive(id, state) {
      this.loading = true;
      this.$apollo
        .mutate({
          mutation: InstructionSetIsActiveQuery,
          variables: { id: id, state: state },
        })
        .then(() => this.$apollo.queries.instructions.refresh())
        .finally(() => {
          this.loading = false;
        });
    },
    forceRereading(item) {
      this.forceRereadingDialog = {
        instruction: item,
        show: true,
      };
    },
  },

  apollo: {
    instructions: {
      query: InstructionsByIsActiveQuery,
      variables() {
        return {
          isActive: this.showActive,
        };
      },
      update(data) {
        return (
          data.instructions &&
          data.instructions.map((i) => ({
            ...i,
            search: [
              i.id,
              i.version.localizedFile.name,
              i.code,
              ...i.tagsGroups.flat().map((t) => t.name),
              ...i.tagsGroups.flat().map((t) => t.description),
              ...i.modes.map((m) => m.name),
            ]
              .join("|")
              .toLowerCase(),
          }))
        );
      },
    },
  },
};
</script>
